import { zoneScene, type DesignAreaAnswer, type DesignLayer } from '@pz/scene';
import type { DesignerCtx } from './panels.js';

/**
 * A small picture of a design, drawn by the real renderer for THIS zone — so
 * a template card or an arrangement shows the shopper's own aspect ratio,
 * photo and words rather than a stock thumbnail that lies about the crop.
 *
 * Shared by the Šabloni catalogue and the Rasporedi strip. Pictures are cached
 * per designer by content (layer ids stripped), so repainting a strip after an
 * unrelated edit costs nothing.
 */

const thumbnailCaches = new WeakMap<DesignerCtx, Map<string, ImageData>>();
/** One render at a time per designer, so a gallery never starves the canvas. */
const queues = new WeakMap<DesignerCtx, Promise<unknown>>();

export async function paintThumb(
  ctx: DesignerCtx,
  thumb: HTMLCanvasElement,
  answer: DesignAreaAnswer,
  cancelled: () => boolean,
  pixelsWide = 160,
  withSticker = false,
): Promise<boolean> {
  const raw = ctx.renderer.renderRaw;
  if (!raw) return false;
  // On the product's own colour, so white words on a navy shirt show up.
  // A sticker prints on white whatever the shirt, so it keeps the light backdrop.
  const rgb = answer.sticker ? undefined : ctx.productRgb?.();
  if (rgb) thumb.style.background = `rgb(${rgb.map(Math.round).join(', ')})`;
  // Catalogue cards show the layout artwork. The live canvas computes the
  // selected design's cut shape; tracing production backing for every tiny
  // card would block typing and tool changes while Sticker is enabled.
  const artwork = withSticker ? answer : { ...answer, sticker: undefined };
  const key = pixelsWide + JSON.stringify({ ...artwork, layers: artwork.layers.map(({ id: _id, ...layer }) => layer) });
  let cache = thumbnailCaches.get(ctx);
  if (!cache) { cache = new Map(); thumbnailCaches.set(ctx, cache); }
  const draw = (pixels: ImageData) => {
    if (cancelled()) return;
    thumb.width = pixels.width; thumb.height = pixels.height;
    thumb.getContext('2d')?.putImageData(pixels, 0, 0);
  };
  const cached = cache.get(key);
  if (cached) { draw(cached); return true; }
  const work = (queues.get(ctx) ?? Promise.resolve()).then(async (): Promise<boolean> => {
    if (cancelled()) return false;
    try {
      await Promise.all(answer.layers
        .filter((layer): layer is Extract<DesignLayer, { kind: 'text' }> => layer.kind === 'text')
        .map((layer) => ctx.fonts.ensure(layer.fontId)));
      // Yield between gallery work so native pointer/keyboard events can run.
      await new Promise<void>(resolve => window.setTimeout(resolve, 0));
      if (cancelled()) return false;
      const scene = zoneScene(withSticker ? ctx.rawConfig : { ...ctx.rawConfig, sticker: undefined }, artwork, pixelsWide / ctx.config.sizeMm.w);
      const out = await raw.call(ctx.renderer, scene, 1);
      if (cancelled() || !out) return false;
      const bytes = new Uint8ClampedArray(out.pixels.byteLength);
      bytes.set(out.pixels);
      const pixels = new ImageData(bytes, out.width, out.height);
      cache!.set(key, pixels);
      if (cache!.size > 120) cache!.delete(cache!.keys().next().value!);
      draw(pixels);
      return true;
    } catch {
      return false;
    }
  });
  queues.set(ctx, work.catch(() => undefined));
  return work;
}
